import React, { useCallback, useEffect, useState } from "react";
import {
    Box, Button, Chip, IconButton, Stack, Table, TableBody, TableCell,
    TableHead, TableRow, Tooltip, Typography,
} from "@mui/material";
import RefreshRoundedIcon     from "@mui/icons-material/RefreshRounded";
import LinkRoundedIcon        from "@mui/icons-material/LinkRounded";
import dayjs from "dayjs";

import ComboBox        from "../../../component/common/ComboBox";
import LoadingDots     from "../../../component/common/LoadingDots";
import TablePaginator, { DEFAULT_PAGE_SIZE } from "../../../component/common/TablePaginator";
import { getApiErrorMessage, runApiAction, serverMsgOr } from "../../../util/apiFeedback";
import { notify } from "../../../util/toast";
import { ds } from "../../../styles/designSystem";
import { STATUS_META } from "../approvalConstants";
import { approvalApi } from "../api/approvalApi";
import { channelApi }  from "../../channel/api/channelApi";

/**
 * 프로젝트 연결 — 프로젝트가 «이름만» 적힌 옛 결재를 실제 프로젝트 채널에 붙이는 화면.
 *
 * <h3>왜 이 화면이 있나</h3>
 * 프로젝트 선택을 필수화하기 전에는 채널명을 직접 타이핑했다. 그 문서들은 channel_name 만 있고
 * channel_id 가 비어 있어서, 화면엔 프로젝트가 적혀 있는데 프로젝트별 지출 취합에는 한 건도 안 잡힌다.
 * 눈으로는 멀쩡해 보여서 아무도 신고하지 않는 종류의 누락이다.
 *
 * <h3>회수-재상신 대신 이걸 쓴다</h3>
 * 승인된 결재를 회수했다 다시 올리면 확정 원장이 지워졌다 새로 생긴다 — 회계 기록이 흔들린다.
 * 여기서 고치면 문서의 프로젝트 표시만 바뀌고 원장·연차·연동 링크는 그대로다.
 *
 * <h3>이미 지출확정된 건은 여기서 손익까지 옮겨지지 않는다</h3>
 * 프로젝트 손익은 결재가 아니라 <b>확정 원장</b>의 채널로 집계한다. 확정 전 문서는 이 화면만으로
 * 온전히 해결되고, 확정된 문서는 표시만 바뀐다. 그래서 안내문을 상단에 고정해 둔다.
 *
 * @param {Function} [onCountChange] 미연결 총건수가 바뀔 때 호출 — 관리자 탭 라벨의 배지를 맞춘다.
 */
export default function ProjectLinkPanel({ onCountChange }) {
    const [rows, setRows]         = useState([]);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading]   = useState(true);
    /* 저장 중인 행 id — 같은 행 더블클릭으로 두 번 보내는 걸 막는다. */
    const [savingId, setSavingId] = useState(null);
    /* 행별로 고른 프로젝트. 저장 전까지 화면에만 있는 값이라 목록 데이터와 섞지 않는다. */
    const [picked, setPicked]     = useState({});

    const [page, setPage]                   = useState(1);
    const [totalPages, setTotalPages]       = useState(1);
    const [totalElements, setTotalElements] = useState(0);

    const load = useCallback((p = 1) => {
        setLoading(true);
        approvalApi.unlinkedProjects(p - 1, DEFAULT_PAGE_SIZE)
            .then((res) => {
                const list = Array.isArray(res?.content) ? res.content : [];
                setRows(list);
                setTotalPages(res?.totalPages ?? 1);
                const total = res?.totalElements ?? list.length;
                setTotalElements(total);
                onCountChange?.(total);
                setPicked({});
            })
            .catch((e) => notify(serverMsgOr("미연결 결재를 불러오지 못했습니다.")(getApiErrorMessage(e)), "error"))
            .finally(() => setLoading(false));
    }, [onCountChange]);

    useEffect(() => { load(page); }, [page, load]);

    /* 보관된(끝난) 프로젝트까지 받는다 — 이 화면은 «이미 나간 지출을 원래 프로젝트에 붙이는» 사후
       정리라 대상이 끝난 프로젝트인 경우가 오히려 많다. 상신 화면은 반대로 활성만 받는다.
       라벨에 «(보관됨)» 을 달아 두지 않으면 끝난 프로젝트인 줄 모르고 고르게 된다. */
    useEffect(() => {
        channelApi.projectOptions(true)
            .then((res) => {
                const list = Array.isArray(res) ? res : (res?.data ?? []);
                setProjects(list.map((c) => ({
                    value: c.id,
                    label: c.archived ? `${c.name} (보관됨)` : c.name,
                })));
            })
            .catch(() => notify("프로젝트 목록을 불러오지 못했습니다.", "error"));
    }, []);

    const save = (row) => {
        const channelId = picked[row.id];
        if (channelId == null || channelId === "") {
            notify("옮길 프로젝트를 먼저 선택해주세요.", "warning");
            return;
        }
        setSavingId(row.id);
        runApiAction({
            request: () => approvalApi.reassignProject(row.id, channelId),
            /* 확정 원장까지 옮긴 건은 지난 결산 숫자가 바뀌므로 그 사실을 토스트에 명시한다 —
               «표시만 바뀐 것»과 구분되지 않으면 결산을 다시 볼지 판단할 수가 없다. */
            successMsg: (data) => {
                const name = data?.channelName ?? "선택한 프로젝트";
                return data?.movedLedgerCount > 0
                    ? `«${row.title}» 을(를) ${name} 로 연결했습니다. 지출확정 건이라 손익 집계도 함께 옮겼습니다.`
                    : `«${row.title}» 을(를) ${name} 로 연결했습니다.`;
            },
            errorMsg: serverMsgOr("프로젝트 연결에 실패했습니다."),
            onSuccess: () => {
                /* 연결되면 더 이상 «미연결» 이 아니라 목록에서 빠진다. 전체 재조회를 하면 페이지가
                   통째로 다시 그려져 다른 행에서 고르던 선택이 날아가므로 그 행만 걷어낸다. */
                setRows((prev) => prev.filter((r) => r.id !== row.id));
                const remaining = Math.max(totalElements - 1, 0);
                setTotalElements(remaining);
                onCountChange?.(remaining);
                setPicked((prev) => {
                    const next = { ...prev };
                    delete next[row.id];
                    return next;
                });
            },
        }).finally(() => setSavingId(null));
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* 화면 이름·나가기는 위쪽 관리자 탭(ApprovalAdminTabs)이 맡는다 — 여기는 새로고침만. */}
            <Stack direction="row" alignItems="center" justifyContent="flex-end">
                <Tooltip title="새로고침">
                    <IconButton size="small" onClick={() => load(page)} disabled={loading}>
                        <RefreshRoundedIcon sx={{ fontSize: 18, color: "#2563eb" }} />
                    </IconButton>
                </Tooltip>
            </Stack>

            {/* 안내 — 무엇이 바뀌고 무엇이 안 바뀌는지 먼저 말한다 */}
            <Box sx={{ bgcolor: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 1, px: 1.5, py: 1.25 }}>
                <Typography sx={{ fontSize: 12, color: "#475569", lineHeight: 1.7 }}>
                    프로젝트에 연결되지 않아 <b>프로젝트별 지출 취합에서 빠지는</b> 결재입니다 —
                    이름만 적혀 있는 문서와, 프로젝트를 아예 안 고른 금액 있는 문서 둘 다입니다.<br />
                    연결하면 <b>이미 지출확정된 건도 손익까지 함께 옮겨집니다.</b>
                    금액·거래일·계좌·자금일보·연차·재무 연동 링크는 바뀌지 않습니다 —
                    다만 지난 결산에 반영된 프로젝트별 숫자는 달라질 수 있습니다.
                </Typography>
            </Box>

            <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                미연결 {totalElements}건
            </Typography>

            {loading ? <LoadingDots /> : (
                <Box>
                    <Table size="small" sx={{ ...ds.tableSx, minWidth: 0, width: "100%" }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>제목</TableCell>
                                <TableCell>기안자</TableCell>
                                <TableCell>금액</TableCell>
                                <TableCell>현재 적힌 이름</TableCell>
                                <TableCell>상태</TableCell>
                                <TableCell>등록일</TableCell>
                                <TableCell sx={{ minWidth: 220 }}>프로젝트</TableCell>
                                <TableCell>연결</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 6, color: "#94a3b8", fontSize: 13 }}>
                                        미연결 결재가 없습니다.
                                    </TableCell>
                                </TableRow>
                            ) : rows.map((r) => {
                                const m = STATUS_META[r.status] || STATUS_META.PENDING;
                                return (
                                    <TableRow key={r.id} hover
                                        sx={{ "& .MuiTableCell-root": { borderBottom: "1px solid #f1f5f9" } }}>
                                        <TableCell align="center" sx={{ fontWeight: 600, color: "#1e293b" }}>{r.title}</TableCell>
                                        <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                                            {r.submitterName || "—"}
                                            {r.submitterDept
                                                ? <Box component="span" sx={{ color: "#94a3b8", fontSize: 11, ml: 0.5 }}>· {r.submitterDept}</Box>
                                                : null}
                                        </TableCell>
                                        <TableCell align="center" sx={{ whiteSpace: "nowrap", fontWeight: 600, color: "#0f172a" }}>
                                            {r.totalAmount != null ? `${Number(r.totalAmount).toLocaleString("ko-KR")}원` : "—"}
                                        </TableCell>
                                        {/* 적힌 이름이 있으면 어느 프로젝트인지 판단할 유일한 단서라 눈에 띄게 두고,
                                            아예 없는 건은 «단서가 없다»는 사실 자체를 구분해 보여 준다
                                            (제목·기안자·금액으로 추론해야 하는 건들이다). */}
                                        <TableCell align="center" sx={{
                                            color: r.channelName ? "#b45309" : "#94a3b8",
                                            fontWeight: r.channelName ? 600 : 500,
                                            fontSize: r.channelName ? 13 : 12,
                                        }}>
                                            {r.channelName || "프로젝트 없음"}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip label={m.label} size="small" sx={{ bgcolor: m.bg, color: m.color, fontWeight: 700 }} />
                                        </TableCell>
                                        <TableCell align="center" sx={{ color: "#64748b", fontSize: 12, whiteSpace: "nowrap" }}>
                                            {r.createdAt ? dayjs(r.createdAt).format("YYYY.MM.DD") : "—"}
                                        </TableCell>
                                        <TableCell align="center">
                                            <ComboBox
                                                options={projects}
                                                value={picked[r.id] ?? ""}
                                                onChange={(v) => setPicked((prev) => ({ ...prev, [r.id]: v }))}
                                                placeholder="프로젝트 선택"
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Button
                                                size="small"
                                                variant="contained"
                                                disableElevation
                                                startIcon={<LinkRoundedIcon sx={{ fontSize: 16 }} />}
                                                disabled={savingId === r.id || !picked[r.id]}
                                                onClick={() => save(r)}
                                                sx={{ fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}
                                            >
                                                연결
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                    <TablePaginator
                        totalPages={totalPages}
                        total={totalElements}
                        pageSize={DEFAULT_PAGE_SIZE}
                        page={page}
                        onPageChange={setPage}
                    />
                </Box>
            )}
        </Box>
    );
}
